/**
 * Cleans assistant text WHILE IT IS STREAMING, so the raw markup that OpenAI emits inline
 * never flashes before the fully-resolved message swaps in on `completed`.
 *
 * The backend resolves file/URL citations only once the response completes (that path lives in
 * ResolveFileLinksInText, ResponseModels.cs) because resolution needs the annotation list, which
 * arrives at the end. During streaming we have no annotations, so we cannot turn `sandbox:` URLs
 * into real `/file?` links — but we CAN remove the two things that render as visible garbage:
 *
 *   1. Inline citation markers (need no annotations — pure text noise):
 *        - CJK-bracket file-search citations, delimited by U+3010 (【) and U+3011 (】)
 *        - Private-use "filecite" tokens (render as square boxes): a U+E200 ... U+E201 span,
 *          with U+E202 separators inside
 *   2. Unresolvable `sandbox:` file references, which otherwise render as a BROKEN <img>
 *      (`![alt](sandbox:...png)`) or a dead link. We collapse them to their label text — the
 *      same "unknown file -> keep the label" behavior the backend applies to sandbox links it
 *      can't resolve — and the real image/link appears when the resolved message swaps in.
 *
 * A trailing *incomplete* marker still being streamed (a lone opening bracket, a dangling
 * U+E200 run, or a half-typed `[label](sandbox:...` with no closing paren yet) is held back —
 * sliced off the end — so it never appears for the one render before the rest of it arrives.
 * Because this runs over the FULL accumulated buffer on every render, markers that span multiple
 * deltas are handled naturally, and the held-back tail reappears cleaned once its closing
 * delimiter streams in.
 *
 * The marker patterns here are intentionally kept in sync with the backend stripping in
 * ResolveFileLinksInText; if one side gains a new marker form, update both.
 */

// Private-use citation delimiters: U+E200 (start), U+E201 (end); the range end U+E20F covers
// stray separators/leftovers. Built via char codes so the source stays plain ASCII.
const PUA_START = String.fromCharCode(0xe200);
const PUA_END = String.fromCharCode(0xe201);
const PUA_RANGE_END = String.fromCharCode(0xe20f);
// CJK brackets used for the other citation form: U+3010 (【) ... U+3011 (】).
const CJK_OPEN = "【";
const CJK_CLOSE = "】";

export const sanitizeStreamingText = (text: string): string => {
    if (!text) return text;

    let out = text;

    // --- 1. Hold back a trailing, still-incomplete marker so it never flashes. ---
    // An `sandbox:` URL whose closing delimiter (`)`, whitespace, quote, `]`) hasn't streamed yet.
    const lastSandbox = out.toLowerCase().lastIndexOf("sandbox:");
    if (lastSandbox !== -1 && !/[)\s\]"']/.test(out.slice(lastSandbox + "sandbox:".length))) {
        // Cut from the start of its markdown wrapper if it is inside one (`[label](sandbox:...`
        // or `![alt](sandbox:...`), otherwise from the bare token.
        const bracket = out.lastIndexOf("[", lastSandbox);
        const isLink = bracket !== -1 && out.slice(bracket, lastSandbox).includes("](");
        let cut = isLink ? bracket : lastSandbox;
        if (isLink && cut > 0 && out[cut - 1] === "!") cut -= 1; // include the leading image "!"
        out = out.slice(0, cut);
    }
    // An unclosed CJK-bracket marker at the end.
    const openCjk = out.lastIndexOf(CJK_OPEN);
    if (openCjk !== -1 && out.indexOf(CJK_CLOSE, openCjk) === -1) {
        out = out.slice(0, openCjk);
    }
    // An unclosed private-use marker run at the end.
    const openPua = out.lastIndexOf(PUA_START);
    if (openPua !== -1 && out.indexOf(PUA_END, openPua) === -1) {
        out = out.slice(0, openPua);
    }

    // --- 2. Strip complete citation markers. ---
    out = out.replace(new RegExp(`${CJK_OPEN}[^${CJK_CLOSE}]*${CJK_CLOSE}`, "g"), "");
    out = out.replace(new RegExp(`${PUA_START}[^${PUA_END}]*${PUA_END}`, "g"), "");
    // Drop any stray leftover chars in the private-use citation range (U+E200 - U+E20F).
    out = out.replace(new RegExp(`[${PUA_START}-${PUA_RANGE_END}]`, "g"), "");

    // --- 3. Neutralize unresolvable sandbox: references (keep the label, drop the dead URL). ---
    out = out.replace(/!?\[([^\]]*)\]\(\s*sandbox:[^)]*\)/gi, "$1");
    // Bare sandbox tokens not inside a markdown link.
    out = out.replace(/sandbox:[^\s)\]"']+/gi, "");

    return out;
};
